/* Internal dependencies */
import { providerToProviderCode as eventsProviderToProviderCode } from 'src/events/Events';
import { providerToProviderCode as artistProviderToProviderCode} from 'src/artist/ArtistDetail';
import User from 'src/types/User';
import Artist from 'src/types/Artist';
import Community from 'src/types/Community';
import Venue from 'src/types/Venue';

export const uriToUrlPath = (uri: string): string | null => {
  try {
    const [scheme, path] = uri.split('://');

    if (scheme) {
      if (scheme === 'beatmatch') {
        if (path.startsWith('events')) {
          const [, eventId] = path.split('/');
          const [, provider, providerId] = eventId.split(':');

          if (eventId.startsWith('new')) { // Order is important here to ensure "new" is not seen as an eventId
            return `/events/new`;
          } else if (provider && providerId) {
            return `/events/${eventsProviderToProviderCode[provider]}/${providerId}`;
          }
        } else if (path.startsWith('event')) { // Order matters here. Plural must come first
          // Event dashboard (i.e. "/event/{{EVENT_ID}}/manage/insights")
          const [, eventId,, initialTabName] = path.split('/'); 
          const [, provider, providerId] = eventId.split(':');

          if (provider && providerId) {
            return `/event/${eventsProviderToProviderCode[provider]}/${providerId}/manage/${initialTabName}`;
          }
        } else if (path.startsWith('posts')) {
          const [, postId] = path.split('/');
          const [, id] = postId.split(':');

          if (postId.startsWith('new')) { // Order is important here to ensure "new" is not seen as an postId
            // TODO: Open create post modal
          } else if (postId) {
            return `/posts/${id}`;
          }
        } else if (path === 'browse' || path === 'explore') {
          return `/explore`;
        } else if (
          path.startsWith('activities') ||
          path.startsWith('meetups') ||
          path.startsWith('plans')
        ) {
          const [, activityId] = path.split('/');
          const [, id] = activityId.split(':');

          if (activityId.startsWith('new')) { // Order is important here to ensure "new" is not seen as an activityId
            return `/plans/new`;
          } else if (id) {
            return `/plans/${id}`;
          }
        } else if (path.startsWith('artists')) {
          const [, artistId] = path.split('/');
          const [, provider, providerId] = artistId.split(':');

          if (artistId.startsWith('new')) { // Order is important here to ensure "new" is not seen as an collectionId
            return `/artists/new`;
          } else if (provider && providerId) {
            return `/artists/${artistProviderToProviderCode[provider]}/${providerId}`;
          }
        } else if (path.startsWith('artist')) { // Order matters here. Plural must come first
          // Artist dashboard (i.e. "/artist/{{ARTIST_ID}}/manage/insights")
          const [, artistId,, initialTabName] = path.split('/'); 
          const [, provider, providerId] = artistId.split(':');

          if (provider && providerId) {
            return `/artist/${artistProviderToProviderCode[provider]}/${providerId}/manage/${initialTabName}`;
          }
        } else if (path.startsWith('users')) {
          const [, userId] = path.split('/');
          const [,,id] = userId.split(':');

          return ''; // TODO: Update this once user profile page is supported

          if (userId) {
            return `/users/${id}`;
          }
        } else if (path.startsWith('communities')) {
          const [, communityId] = path.split('/'); 
          const [, id] = communityId.split(':');

          if (communityId.startsWith('new')) { // Order is important here to ensure "new" is not seen as an communityId
            return `/communities/new`;
          } else if (id) {
            return `/communities/${id}`;
          }
        } else if (path.startsWith('community')) { // Order matters here. Plural must come first
          // Community dashboard (i.e. "/community/{{COMMUNITY_ID}}/manage/inights")
          const [, communityId,, initialTabName] = path.split('/');
          const [, id] = communityId.split(':');

          if (id) {
            return `/community/${id}/manage/${initialTabName}`;
          }
        } else if (path.startsWith('collections')) {
          const [, collectionId] = path.split('/');
          const [, id] = collectionId.split(':');

          if (collectionId.startsWith('new')) { // Order is important here to ensure "new" is not seen as an collectionId
            return `/collections/new`;
          } else if (id) {
            return `/collections/${id}`;
          }
        } else if (path.startsWith('do-lists')) {
          const [, collectionId] = path.split('/');
          const [, id] = collectionId.split(':');

          if (collectionId.startsWith('new')) { // Order is important here to ensure "new" is not seen as an collectionId
            return `/do-lists/new`;
          } else if (collectionId) {
            return `/do-lists/${id}`;
          }
        } else if (path.startsWith('venues')) {
          const [, venueId] = path.split('/');
          const [, id] = venueId.split(':');

          if (venueId.startsWith('new')) { // Order is important here to ensure "new" is not seen as an collectionId
            return `/venues/new`;
          } else if (id) {
            return `/venues/${id}`;
          }
        } else if (path.startsWith('tickets') || path.startsWith('orders')) { 
          const [, orderId] = path.split('/');
          const [, id] = orderId.split(':');

           if (id) {
            return `/tickets/${id}`;
          } else {
            return `/tickets`;
          }
        }
      }
    }

    return null;
  } catch (e) {
    console.error('Invalid deep link URI:', uri);
    return null;
  }
};

const getProviderAndProviderIdFromId = (id: string = ''): { provider: string; providerId: string; } => {
  const parts = id.split(':');
  return { provider: parts[1], providerId: parts[2] };
};

export function itemToUrlPath(item: User | Artist | Event | Community | Venue): string | null {
  const { type, id } = item as any;
  const { provider, providerId } = getProviderAndProviderIdFromId(id);

  if (!type || !id) return null;

  if (type === 'event') {
    const providerCode = eventsProviderToProviderCode[provider]; // event-specific code
    return `/events/${providerCode}/${providerId}`;
  }

  if (type === 'artist') {
    const providerCode = artistProviderToProviderCode[provider]; // artist-specific code
    return `/artists/${providerCode}/${providerId}`;
  }

  if (type === 'activity') {
    return `/plans/${id.replace(/^activity:/, '')}`;
  }

  if (type === 'feedPost') {
    return `/posts/${id.replace(/^feedPost:/, '')}`;
  }

  if (type === 'community') {
    return `/communities/${id.replace(/^community:/, '')}`;
  }

  if (type === 'collection') {
    return `/collections/${id.replace(/^collection:/, '')}`;
  }

  if (type === 'venue') {
    return `/venues/${id.replace(/^venue:/, '')}`;
  }

  if (type === 'user') {
    return ''; // TODO: Update this once user profile page is supported
    return `/users/${id.replace(/^user:us-west-2:/, '')}`;
  }

  if (type === 'order') {
    return `/tickets/${id.replace(/^order:/, '')}`;
  }

  return null;
}
